import { isLastPage, QueryResources, QueryResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { MutationFn } from '@/lib/datx-jsonapi-react/interfaces/MutaionFn';
import { Comment } from '@/resources/Comment';
import { AppCollection } from '@/stores/AppStore';
import { IConversationMessage } from '@/utils/conversation/types';

export type CommentsScope = 'roots' | 'only_with_distinct_users' | null;

export interface IQueryRootLectureCommentsVariables {
  lectureId?: string;
}

export const queryLectureRootCommentsInfinite = ({
  lectureId,
}: IQueryRootLectureCommentsVariables): QueryResourcesInfinite<Comment> => {
  if (!lectureId) {
    return null;
  }

  return (pageIndex, previousPageData) => {
    if (previousPageData && isLastPage(previousPageData)) {
      return null;
    }

    const pageNumber = pageIndex + 1;
    const filter = { scope: 'roots', lecture_id: lectureId };

    return [
      Comment.type,
      {
        filter,
        params: [
          { key: 'page[size]', value: '5' },
          { key: 'page[number]', value: pageNumber.toString() },
        ],
        sort: '-created_at,-id',
        include: 'user',
      },
    ];
  };
};

export interface IQueryLectureCommentRepliesVariables {
  threadId?: string;
}

export const queryLectureCommentRepliesInfinite = ({
  threadId,
}: IQueryLectureCommentRepliesVariables): QueryResourcesInfinite<Comment> => {
  if (!threadId) {
    return null;
  }

  return (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData?.links?.next) {
      return null;
    }

    const pageNumber = pageIndex + 1;
    const filter = { thread_id: threadId };

    return [
      Comment.type,
      {
        filter,
        params: [
          { key: 'page[size]', value: '5' },
          { key: 'page[number]', value: pageNumber.toString() },
        ],
        sort: 'created_at,-id',
        include: 'user',
      },
    ];
  };
};

export interface IQueryLectureCommentVariables {
  lectureId?: string;
  threadId?: string;
}

export const queryLectureCommentsInfinite = ({ lectureId, threadId }: IQueryLectureCommentVariables) => {
  if (threadId) {
    return queryLectureCommentRepliesInfinite({ threadId });
  } else {
    return queryLectureRootCommentsInfinite({ lectureId });
  }
};

export interface ICreateRootLectureCommentInput {
  store: AppCollection;
  lectureId?: string;
  threadId?: string;
  content: string;
}

export const createLectureComment: MutationFn<ICreateRootLectureCommentInput, Comment> = (
  store,
  { lectureId, threadId, content }: ICreateRootLectureCommentInput
) => {
  const lectureLinkage = lectureId
    ? {
        lecture: {
          data: {
            type: 'lectures',
            id: lectureId,
          },
        },
      }
    : {};

  const threadLinkage = threadId
    ? {
        thread: {
          data: {
            type: 'comments',
            id: threadId,
          },
        },
      }
    : {};

  return store.request(`comments`, 'POST', {
    data: {
      type: 'comments',
      attributes: {
        content,
      },
      relationships: {
        ...lectureLinkage,
        ...threadLinkage,
      },
    },
  });
};

export interface IDestroyCommentInput {
  store: AppCollection;
  comment: Comment;
}

export const destroyComment: MutationFn<IDestroyCommentInput, Comment> = (store, { comment }) => {
  return store.request(`comments/${comment.id}`, 'DELETE');
};

export interface IDestroyMessageInput {
  store: AppCollection;
  message: IConversationMessage;
}

export const destroyMessage: MutationFn<IDestroyMessageInput, Comment> = (store, { message }) => {
  return store.request(`chat_messages/${message}?channel_id=${message.conversation.sid}`, 'DELETE');
};

export const queryDistinctLectureComments = (commentId: string): QueryResources<Comment> =>
  commentId && [
    Comment,
    { filter: { scope: 'only_with_distinct_users', thread_id: commentId }, include: 'user', sort: '-created_at,-id' },
  ];
